import type { QuoteBudgetLine } from "../types";

export {
  ITINERARY_RESOURCE_QUICK_EXAMPLES as QUOTE_QUICK_EXAMPLES,
  ITINERARY_RESOURCE_SCENE_ORDER as QUOTE_RESOURCE_SCENE_ORDER,
  itineraryResourceSceneMeta as sceneMetaFor,
  quickExamplesForItineraryResource as quickExamplesFor,
} from "../../../shared/itinerary-resource-entry";
export type {
  ItineraryResourceSceneMeta as QuoteSceneMeta,
} from "../../../shared/itinerary-resource-entry";

const TRANSPORT_TYPE_ORDER = ["飞机", "船", "火车", "高铁", "动车", "汽车", "其他"];
const BUS_TYPE_ORDER = ["单接", "单送", "接送", "包车", "其他"];
const TICKET_TYPE_ORDER = [
  "首道门票", "门票", "小景点", "大索道", "索道", "景区车", "游船", "小火车", "讲解服务", "体验/课程", "表演", "其他",
];
const TRAVELER_ROLE_ORDER = ["学生", "老师", "导游", "司机"];

/** 在有序词表中的位置，未命中排到末尾。 */
function orderIndex(order: readonly string[], value: string | null | undefined): number {
  const index = order.indexOf(String(value ?? ""));
  return index === -1 ? order.length : index;
}

/** 行的资源子类型（交通类型/用车类型/票种），优先 resourceEntryType。 */
function lineSubtype(line: QuoteBudgetLine): string {
  return String(line.resourceEntryType ?? line.itemSpecSnapshot ?? "");
}

/** 行的展示名称（住宿区域/线路/点位）。 */
function lineName(line: QuoteBudgetLine): string {
  return String(line.itemNameSnapshot ?? "").trim();
}

/** 从 `D10 xxx` 这类名称提取数字日号，避免字符串排序把 D10 排在 D2 前。 */
function dayNumber(line: QuoteBudgetLine): number {
  const matched = lineName(line).match(/^D(\d+)\b/i);
  return matched ? Number(matched[1]) : Number.MAX_SAFE_INTEGER;
}

/** 行的角色文案，用于角色顺序排序。 */
function lineRole(line: QuoteBudgetLine): string {
  return String(line.travelerRole ?? "").trim();
}

/** 标准角色之后依次排列未指定角色和未知角色，避免历史异常值穿插。 */
function roleOrderIndex(role: string): number {
  if (!role) return TRAVELER_ROLE_ORDER.length;
  const index = TRAVELER_ROLE_ORDER.indexOf(role);
  return index === -1 ? TRAVELER_ROLE_ORDER.length + 1 : index;
}

/** 把门票、耳麦等点位明细归属到 entry_options.points 中的原始点位. */
function pointOrderIndex(pointOrder: readonly string[], line: QuoteBudgetLine): number {
  const name = lineName(line);
  const exactIndex = pointOrder.indexOf(name);
  if (exactIndex >= 0) return exactIndex;
  const matched = pointOrder
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => name.startsWith(point))
    .sort((left, right) => right.point.length - left.point.length)[0];
  return matched?.index ?? pointOrder.length;
}

/** 同类明细先按项目名称或业务主键分组，同名项目内再按旅客角色排列。 */
function compareSameType(
  a: QuoteBudgetLine,
  b: QuoteBudgetLine,
  pointOrder: readonly string[] | undefined,
): number {
  const roleA = lineRole(a);
  const roleB = lineRole(b);
  if (a.resourceType === "考察点" && b.resourceType === "考察点" && pointOrder) {
    const pointComparison = pointOrderIndex(pointOrder, a) - pointOrderIndex(pointOrder, b);
    if (pointComparison !== 0) return pointComparison;
  }
  if (a.resourceType === "包车" && b.resourceType === "包车") {
    const dayComparison = dayNumber(a) - dayNumber(b);
    if (dayComparison !== 0) return dayComparison;
  }
  const nameA = lineName(a);
  const nameB = lineName(b);
  if (!nameA && nameB) return 1;
  if (nameA && !nameB) return -1;
  const nameComparison = nameA.localeCompare(nameB, "zh-CN");
  if (nameComparison !== 0) return nameComparison;
  const roleComparison = roleOrderIndex(roleA) - roleOrderIndex(roleB);
  if (roleComparison !== 0) return roleComparison;
  const subtypeComparison = a.resourceType === "大交通"
    ? orderIndex(TRANSPORT_TYPE_ORDER, lineSubtype(a)) - orderIndex(TRANSPORT_TYPE_ORDER, lineSubtype(b))
    : a.resourceType === "包车"
      ? orderIndex(BUS_TYPE_ORDER, lineSubtype(a)) - orderIndex(BUS_TYPE_ORDER, lineSubtype(b))
      : a.resourceType === "考察点"
        ? orderIndex(TICKET_TYPE_ORDER, lineSubtype(a)) - orderIndex(TICKET_TYPE_ORDER, lineSubtype(b))
        : lineSubtype(a).localeCompare(lineSubtype(b), "zh-CN");
  return subtypeComparison || a.lineNo - b.lineNo || a.budgetLineId.localeCompare(b.budgetLineId);
}

/**
 * 生成展示排序比较器：按项目归组及资源类型专属规则排列，新加入状态仅显示徽标。
 * @param recentLineId 最近新增行标识，仅保留兼容调用，不参与排序
 */
export function makeLineComparator(
  recentLineId: string | null | undefined,
  pointOrder?: readonly string[],
): (a: QuoteBudgetLine, b: QuoteBudgetLine) => number {
  void recentLineId;
  return (a, b) => compareSameType(a, b, pointOrder);
}
